import { Fragment } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Text } from "@similarweb/ui-components/foundations";

import { MetricsWidget } from "app/pages/digital-marketing/common/components/MetricsWidget/MetricsWidget";
import { useTranslation } from "pro-features/components/WithTranslation";

interface IMetricData {
    value?: number;
    share?: number;
    pop?: number;
    granular?: Array<{ date: string; value?: number }>;
}

interface ICampaignMetricsWidgetsProps {
    data?: {
        allPaidClicks?: IMetricData;
        clicksLost?: IMetricData;
        clickShare?: IMetricData;
    };
    isLoading: boolean;
    domain: string;
    titleComponent?: "text" | "h6";
    size?: "sm" | "md";
}

export const CampaignMetricsWidgets = ({
    data,
    isLoading,
    domain,
    titleComponent = "text",
    size = "sm",
}: ICampaignMetricsWidgetsProps) => {
    const translate = useTranslation();
    const { allPaidClicks, clicksLost, clickShare } = data || {};

    const renderTitle = (text: string) => {
        if (titleComponent === "h6") {
            return (
                <Text
                    fontVariant="h6"
                    color={colorsPalettes.carbon["500"]}
                >
                    {text}
                </Text>
            );
        }
        return text;
    };

    const formatGranularData = (granular?: Array<{ date: string; value?: number }>) => [
        {
            data:
                granular?.map((item) => ({
                    x: new Date(item.date).getTime(),
                    y: item.value ?? 0,
                    original: item.value,
                })) ?? [],
        },
    ];

    return (
        <Fragment>
            <MetricsWidget
                isLoading={isLoading}
                title={renderTitle(translate("monitor_and_protect.homepage.metrics.all_paid_clicks.title"))}
                value={allPaidClicks?.value}
                valueFormatter="toAbbr"
                precision={0}
                helpText={translate("monitor_and_protect.homepage.metrics.all_paid_clicks.help_text")}
                change={allPaidClicks?.pop}
            >
                <MetricsWidget.Progress
                    main={{
                        label: domain,
                        value: allPaidClicks?.share !== undefined ? allPaidClicks.share / 100 : undefined,
                    }}
                    other={{
                        label: translate("monitor_and_protect.homepage.metrics.all_paid_clicks.competitors_label"),
                        value: allPaidClicks?.share !== undefined ? (100 - allPaidClicks.share) / 100 : undefined,
                    }}
                    height={size === "sm" ? 12 : 16}
                />
            </MetricsWidget>
            <MetricsWidget
                isLoading={isLoading}
                title={renderTitle(translate("monitor_and_protect.homepage.metrics.clicks_lost.title"))}
                value={clicksLost?.value}
                valueFormatter="toAbbr"
                precision={0}
                behavior="inverted"
                helpText={translate("monitor_and_protect.homepage.metrics.clicks_lost.help_text")}
                change={clicksLost?.pop}
            >
                <MetricsWidget.Graph
                    formatMode="absolute"
                    data={formatGranularData(clicksLost?.granular)}
                />
            </MetricsWidget>
            <MetricsWidget
                isLoading={isLoading}
                title={renderTitle(translate("monitor_and_protect.homepage.metrics.share_of_clicks.title"))}
                value={clickShare?.value !== undefined ? clickShare.value / 100 : undefined}
                valueFormatter="toPercent"
                precision={0}
                helpText={translate("monitor_and_protect.homepage.metrics.share_of_clicks.help_text")}
                change={clickShare?.pop}
            >
                <MetricsWidget.Graph
                    formatMode="percentage"
                    data={formatGranularData(clickShare?.granular)}
                />
            </MetricsWidget>
        </Fragment>
    );
};
